"use client";

import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const OPTION_UUID = "GUYDEEZ-SEEN-IN";

/**
 * "Vu dans" press strip. Content is fully dynamic — driven by the
 * `GUYDEEZ-SEEN-IN` row of the Options table (latest version = highest id).
 *
 * Parsing rules (negotiated with the admin UX):
 *   - read the `value` field (the `desc` field is reserved for the editor's
 *     own notes)
 *   - split on `;`
 *   - trim leading/trailing whitespace on each segment so spacing in the
 *     admin input doesn't bleed into the marquee
 *   - keep internal whitespace intact (e.g. "TRIP ADVISOR" stays a single
 *     segment with the space preserved)
 *
 * The component renders nothing while loading and nothing if the API fails
 * or returns an empty value.
 */
const SectionPressMarquee: FC<{ className?: string }> = ({ className = "" }) => {
  const { t } = useTranslation("guydeez");
  const [logos, setLogos] = useState<string[] | null>(null);

  useEffect(() => {
    const base = process.env.NEXT_PUBLIC_SOCKET || "";
    if (!base) return;

    // Press strip changes from the admin maybe once a quarter. A 5-minute
    // browser cache is fine — the marketing-perceived freshness is fine and
    // it cuts a request out of every home-page load.
    fetch(`${base}/api/options/uuid/${encodeURIComponent(OPTION_UUID)}`, {
      headers: { Accept: "application/json" },
      cache: "default",
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const value = data?.value;
        if (typeof value !== "string") return;
        const parsed = value
          .split(";")
          .map((s) => s.trim())
          .filter(Boolean);
        setLogos(parsed);
      })
      .catch(() => {
        // Silent: marketing block, no user-actionable error.
      });
  }, []);

  if (!logos || logos.length === 0) return null;

  return (
    <section
      className={`gdx ${className}`}
      style={{
        padding: "22px 0",
        borderTop: "1px solid var(--g-line)",
        borderBottom: "1px solid var(--g-line)",
        background: "var(--g-bg-soft)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 32 }}>
        <span
          className="gdx-eyebrow"
          style={{ flexShrink: 0, paddingLeft: 56, paddingRight: 8 }}
        >
          {t("homepage_press_eyebrow", { defaultValue: "Vu dans" })}
        </span>
        <div
          style={{
            flex: 1,
            position: "relative",
            overflow: "hidden",
            maskImage:
              "linear-gradient(90deg, transparent, black 5%, black 95%, transparent)",
            WebkitMaskImage:
              "linear-gradient(90deg, transparent, black 5%, black 95%, transparent)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 56,
              width: "max-content",
              animation: "gdx-marquee 38s linear infinite",
              opacity: 0.68,
            }}
          >
            {[0, 1].flatMap((dup) =>
              logos.map((s, i) => (
                <span
                  key={`${dup}-${i}`}
                  style={{
                    fontFamily: "var(--g-font-display)",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    fontSize: 13,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    color: "var(--g-ink-700)",
                  }}
                >
                  {s}
                </span>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SectionPressMarquee;
